import * as Yup from "yup";
import {
  formatMoneyCents,
  parseMaskedDollarInput,
  parseMaskedPercentInput,
} from "@/core/utils/money";
import { SpiffRule } from "@/spiff-rules/spiff-rule";

export const SPIFF_RULE_INPUT_LABELS = {
  payment_amount_dollars: "Payout amount",
  payment_percent: "Payout percentage",
  payment_percent_cap_dollars: "Maximum payout",
} as const;

export const spiffRuleValidationSchema = Yup.object().shape({
  payment_type: Yup.string().oneOf(["fixedAmount", "percentage"]).required(),
  payment_amount_dollars: Yup.string()
    .when("payment_type", {
      is: "fixedAmount",
      then: (schema) =>
        schema
          .required("Payout amount is required")
          .test(
            "is-valid-amount",
            "Payout amount must be greater than 0",
            (value) => {
              const parsed = parseMaskedDollarInput(value);
              return parsed !== null && parsed >= 0.01;
            },
          ),
      otherwise: (schema) =>
        schema.test(
          "is-valid-amount",
          "Payout amount must be greater than 0",
          (value) => {
            if (!value) return true;
            const parsed = parseMaskedDollarInput(value);
            return parsed !== null && parsed >= 0.01;
          },
        ),
    })
    .label(SPIFF_RULE_INPUT_LABELS.payment_amount_dollars),
  payment_percent: Yup.string()
    .when("payment_type", {
      is: "percentage",
      then: (schema) =>
        schema
          .required("Payout percentage is required")
          .test(
            "is-valid-percent",
            "Payout percentage must be between 1 and 100",
            (value) => {
              const parsed = parseMaskedPercentInput(value);
              return parsed !== null && parsed >= 1 && parsed <= 100;
            },
          ),
      otherwise: (schema) =>
        schema.test(
          "is-valid-percent",
          "Payout percentage must be between 1 and 100",
          (value) => {
            if (!value) return true;
            const parsed = parseMaskedPercentInput(value);
            return parsed !== null && parsed >= 1 && parsed <= 100;
          },
        ),
    })
    .label(SPIFF_RULE_INPUT_LABELS.payment_percent),
  payment_percent_cap_dollars: Yup.string()
    .test(
      "is-valid-amount",
      "Maximum payout must be greater than 0",
      (value) => {
        if (!value) return true; // Allow empty (optional field)
        const parsed = parseMaskedDollarInput(value);
        return parsed !== null && parsed >= 0.01;
      },
    )
    .optional()
    .label(SPIFF_RULE_INPUT_LABELS.payment_percent_cap_dollars),
});

export interface SpiffRuleFormValues {
  payment_type: "fixedAmount" | "percentage";
  payment_amount_dollars: string;
  payment_percent: string;
  payment_percent_cap_dollars: string;
  active: boolean;
}

export const EMPTY_SPIFF_RULE_FORM_VALUES: SpiffRuleFormValues = {
  payment_type: "fixedAmount",
  payment_amount_dollars: "",
  payment_percent: "",
  payment_percent_cap_dollars: "",
  active: false,
} as const;

export const convertSpiffRuleToFormValues = ({
  payment_amount_cents,
  payment_percent,
  payment_percent_cap_cents,
  active,
}: SpiffRule): SpiffRuleFormValues => {
  // Determine payment type based on which field has a value
  const paymentType: "fixedAmount" | "percentage" = payment_amount_cents
    ? "fixedAmount"
    : payment_percent
      ? "percentage"
      : "fixedAmount"; // Default to fixedAmount

  // Convert cents to dollars and format with dollar sign
  const paymentAmountDollars = formatMoneyCents(payment_amount_cents) ?? "";

  // Format percent with percent sign
  const paymentPercentFormatted = payment_percent ? `${payment_percent}%` : "";

  // Convert cents to dollars and format with dollar sign
  const paymentPercentCapDollars =
    formatMoneyCents(payment_percent_cap_cents) ?? "";

  return {
    payment_type: paymentType,
    payment_amount_dollars: paymentAmountDollars,
    payment_percent: paymentPercentFormatted,
    payment_percent_cap_dollars: paymentPercentCapDollars,
    active: active ?? false,
  };
};

export const convertSpiffRuleFormValuesToParams = ({
  payment_type: _paymentType, // Destructure but don't use (not sent to backend)
  payment_amount_dollars,
  payment_percent,
  payment_percent_cap_dollars,
  active,
}: SpiffRuleFormValues) => {
  // Parse dollar amount and convert to cents
  const parsedDollars = parseMaskedDollarInput(payment_amount_dollars);
  const paymentAmountCents = parsedDollars
    ? Math.round(parsedDollars * 100)
    : null;

  // Parse percent value
  const paymentPercentValue = parseMaskedPercentInput(payment_percent);

  // Parse percent cap and convert to cents
  const parsedPercentCap = parseMaskedDollarInput(payment_percent_cap_dollars);
  const paymentPercentCapCents = parsedPercentCap
    ? Math.round(parsedPercentCap * 100)
    : null;

  return {
    payment_amount_cents: paymentAmountCents,
    payment_percent: paymentPercentValue,
    payment_percent_cap_cents: paymentPercentCapCents,
    active,
  };
};
